import {
  closestCenter,
  DndContext,
  DragEndEvent,
  MouseSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { ColumnDraggable, ColumnInactive, MuiButton } from "@ryde/controls";
import { usePageContext } from "@ryde/redux";
import { tableSettingsDrawerAF } from "@ryde/shared";

import { BackgroundImageDrawer, BaseDrawer } from "../drawers";
import { useTableSettingsSearch } from "./parts";
import { TableSettingsSearch } from "./table-settings-search";
import { ITableSettingsDrawerState } from "./table-settings.models";
import {
  DragItemsContainerStyled,
  TableSettingsContent,
} from "./table-settings.styled";

interface ITableSettingsDrawerInnerProps {
  onVisibilityChange: (
    newState: "on" | "off",
    identifier: string | undefined,
  ) => void;
  state: ITableSettingsDrawerState<object>;
  onDragEnd?(event: DragEndEvent): void;
  onReset(): void;
}

export const TableSettingsDrawerInner: FC<ITableSettingsDrawerInnerProps> = ({
  state,
  onDragEnd,
  onVisibilityChange,
  onReset,
}) => {
  const { t } = useTranslation();
  const { entity } = usePageContext();
  const setTableSettingsDrawerOpened = useSetRecoilState(
    tableSettingsDrawerAF(entity),
  );
  const onCloseClick = useEvent(() => {
    setTableSettingsDrawerOpened(false);
  });

  const { activeColumns, inactiveColumns } = useTableSettingsSearch(state);

  const activeColumnNames = useMemo(() => {
    return activeColumns?.map(x => x.id).filter(x => x !== undefined);
  }, [activeColumns]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
  );

  return (
    <DndContext
      onDragEnd={onDragEnd}
      collisionDetection={closestCenter}
      sensors={sensors}
    >
      <BaseDrawer onCloseDrawer={onCloseClick}>
        <BackgroundImageDrawer
          title={t(`general.MANAGE_COLUMNS`)}
          onCloseDrawer={onCloseClick}
        >
          <TableSettingsContent>
            <MuiButton
              muiVariant="text"
              className="table-setting-drawer__reset-button"
              onClick={onReset}
            >
              {t("view.resetDefaults")}
            </MuiButton>
            <TableSettingsSearch />
            <DragItemsContainerStyled>
              <h3 className="table-setting-drawer__title">
                {t(`general.SHOWING`)}
              </h3>

              <SortableContext
                items={activeColumnNames}
                strategy={verticalListSortingStrategy}
              >
                {activeColumns?.map(row => (
                  <ColumnDraggable
                    item={row}
                    key={row.id}
                    onVisibilityChange={() => onVisibilityChange("off", row.id)}
                  />
                ))}
              </SortableContext>
            </DragItemsContainerStyled>
            <DragItemsContainerStyled className="table-setting-drawer__drag-items-container_inactive">
              <h3>{t(`general.NOT_SHOWING`)}</h3>
              {inactiveColumns?.map(row => (
                <ColumnInactive
                  item={row}
                  key={String(row.id)}
                  onVisibilityChange={() => onVisibilityChange("on", row.id)}
                />
              ))}
            </DragItemsContainerStyled>
          </TableSettingsContent>
        </BackgroundImageDrawer>
      </BaseDrawer>
    </DndContext>
  );
};
